import { ProjectType, projectTypeName } from "../../../common/constants";
import { CsvColumn } from "../reportCsvExport";
import { ItemReportDataExpanded } from "./itemsReportApi";
import { ItemReportDataAggregated } from "./itemsReportModel";

export type ItemUsageExportRow = {
  item: string
  units: string
  projectId: number
  projectName: string
  customerDisplayName: string
  workareaName: string
  estimatedQuantity: number
  actualQuantity: number
  jobType: number
  status: string
}

export const getItemStatusLabel = (project: Pick<ItemReportDataExpanded, 'isApproved' | 'isOnHold' | 'lost'>): string => {
  if (project.lost) {
    return 'Lost'
  }
  if (project.isOnHold) {
    return 'On Hold'
  }
  if (project.isApproved) {
    return 'Approved'
  }
  return 'Draft'
}

// One row per item × project × work area — the detail the table hides in its
// expandable rows. Summing a group of rows gives that item's on-screen totals.
export const flattenItemsForExport = (items: ItemReportDataAggregated[]): ItemUsageExportRow[] =>
  items.flatMap(item => item.relatedProjects.map(project => ({
    item: item.name,
    units: item.units,
    projectId: project.projectId,
    projectName: project.projectName,
    customerDisplayName: project.customerDisplayName,
    workareaName: project.workareaName,
    estimatedQuantity: project.estimatedQuantity,
    actualQuantity: project.actualQuantity,
    jobType: project.jobType,
    status: getItemStatusLabel(project),
  })))

// The export flattens the per-project detail the table hides in its expandable rows, item by item.
export const getItemsExportRows = (items: ItemReportDataAggregated[]): ItemUsageExportRow[] =>
  flattenItemsForExport([...items].sort((a, b) => a.name.localeCompare(b.name) || a.units.localeCompare(b.units)))

export const itemsExportColumns: CsvColumn<ItemUsageExportRow>[] = [
  { title: 'Item', dataIndex: 'item' },
  { title: 'Units', dataIndex: 'units' },
  { title: 'Project', dataIndex: 'projectName' },
  { title: 'Customer', dataIndex: 'customerDisplayName' },
  { title: 'Workarea', dataIndex: 'workareaName' },
  { title: 'Estimated Quantity', dataIndex: 'estimatedQuantity', render: (_, record) => Number(record.estimatedQuantity.toFixed(2)) },
  { title: 'Actual Quantity', dataIndex: 'actualQuantity', render: (_, record) => Number(record.actualQuantity.toFixed(2)) },
  { title: 'Job Type', dataIndex: 'jobType', render: (jobType: ProjectType) => projectTypeName(jobType) },
  { title: 'Status', dataIndex: 'status' },
]
